import { FC, PropsWithChildren } from 'react'

import { match } from 'ts-pattern'
import {
  EmptyState,
  ErrorMessage,
  Inspector,
  IconStackedBarChart,
  Center,
  Flex
} from '@sequencehq/core-components'
import Spinner from 'components/Loading'
import {
  PublishedStatus,
  usePublishedStatus
} from '../../../QuoteEditorSidebar/hooks'
import { Insights, LastSeen } from './sections'
import { useSyncApiAnalyticsToJotai } from './useSyncApiAnalyticsToJotai'

export const InspectorContent: FC = () => {
  const publishedStatus = usePublishedStatus()

  return match(publishedStatus)
    .with(
      PublishedStatus.Published,
      PublishedStatus.PreparingSignatureRequest,
      PublishedStatus.ReadyToSign,
      PublishedStatus.ProcessingSignatures,
      PublishedStatus.Accepted,
      PublishedStatus.Executed,
      () => <AnalyticsPublishedState />
    )
    .with(
      PublishedStatus.NotPublished,
      PublishedStatus.WaitingForApproval,
      () => <AnalyticsUnpublishedState />
    )
    .with(PublishedStatus.Template, PublishedStatus.Archived, () => null)
    .exhaustive()
}

const AnalyticsUnpublishedState: FC = () => {
  return (
    <Wrapper>
      <EmptyState
        icon={IconStackedBarChart}
        iconSize="sm"
        title="Analytics"
        body="Publish quote to view analytics"
        data-testid="quote.inspector.analytics.unavailable"
      />
    </Wrapper>
  )
}

const AnalyticsPublishedState: FC = () => {
  const { isLoading, isError } = useSyncApiAnalyticsToJotai()

  if (isError) return <ErrorState />
  if (isLoading) return <LoadingState />

  return (
    <Wrapper>
      <Flex gap="16px" flexDirection="column">
        <Insights />
        <LastSeen />
      </Flex>
    </Wrapper>
  )
}

const Wrapper: FC<PropsWithChildren> = ({ children }) => (
  <Inspector.Content data-testid="quote.inspector.analytics">
    {children}
  </Inspector.Content>
)

const LoadingState: FC = () => (
  <Wrapper>
    <Center width="100%" height="100%">
      <Spinner />
    </Center>
  </Wrapper>
)

const ErrorState: FC = () => (
  <Wrapper>
    <Center width="100%" height="100%" textAlign="center">
      <ErrorMessage data-testid="quote.inspector.analytics.errorMessage" />
    </Center>
  </Wrapper>
)
